import { Skill, ProjectStack } from '@shared/types';
import {
  EmployeeEducation,
  EmployeeCertification,
  EmployeeSupporter,
} from './employee';
import { ActionType } from '@/constants/action';

export enum MessageType {
  SUCCESS = 'success',
  ERROR = 'error',
}

// Generic constraint for item collections (skills, technical stacks)
export type ItemCollection = {
  name?: string;
  items?: string[];
};

// Generic result type for item collection updates (skills, technical stacks)
export type ItemCollectionUpdateResult<T extends ItemCollection> = {
  successfulAdd: T[];
  successfulRemove: T[];
  normalizedMessage: string;
  hasSuccessfulOperations: boolean;
  message: { type: MessageType; text: string };
};

export type InvalidItemsData = {
  collectionsNotFound: string[];
  itemsNotFound: Map<string, string[]>;
  itemsAlreadyExisting: Map<string, string[]>;
};

export type EmployeeSkillsUpdateResult = {
  successfulAddSkills: Skill[];
  successfulRemoveSkills: Skill[];
  normalizedMessage: string;
  hasSuccessfulOperations: boolean;
  message: {
    type: MessageType;
    text: string;
  };
};

export type ProjectTechStackUpdateResult = {
  successfulAddStacks: ProjectStack[];
  successfulRemoveStacks: ProjectStack[];
  normalizedMessage: string;
  hasSuccessfulOperations: boolean;
  message: {
    type: MessageType;
    text: string;
  };
};

export enum FailureReason {
  ALREADY_EXISTS = 'alreadyExists',
  NOT_FOUND = 'notFound',
  INVALID_DATES = 'invalidDates',
  INVALID_URLS = 'invalidUrls',
}

export type FailureConfig = {
  pattern: string;
  reason: FailureReason;
  operation: ActionType;
};

export type UpdateOperationResult<TItem> = {
  showCard: boolean;
  cardData: TItem[];
  message: string;
  messageType: MessageType;
};

export type ParsedError = {
  reason: FailureReason;
  operation: ActionType;
  text: string;
};

// Education types
export type EducationFailure = {
  education: EmployeeEducation;
  reason: FailureReason;
};

export type EducationUpdateResult = UpdateOperationResult<EmployeeEducation>;

// Certification types
export type CertificationFailure = {
  certification: EmployeeCertification;
  reason: FailureReason;
};

export type CertificationUpdateResult =
  UpdateOperationResult<EmployeeCertification>;

// Training card types
export type TrainingCardFieldsConfig = {
  showTopicLabel: boolean;
  showStatus: boolean;
  showStart: boolean;
  showEstEnd: boolean;
  showActualEnd: boolean;
  showSupporters: boolean;
  showCard: boolean;
};

export type PickedTrainingCardData = {
  user: string;
  topicLabel?: string;
  status?: string;
  start?: string;
  estEnd?: string;
  actualEnd?: string | null;
  supporters?: Partial<EmployeeSupporter>[];
};

export type PickTrainingCardFieldsParams = Pick<
  TrainingCardFieldsConfig,
  | 'showTopicLabel'
  | 'showStatus'
  | 'showStart'
  | 'showEstEnd'
  | 'showActualEnd'
  | 'showSupporters'
> & {
  newValueTopic?: string;
  resultTopicLabel?: string;
  statusFromArgs?: string;
  startDateFromArgs?: string;
  estEndDateFromArgs?: string;
};

// Presentation types
export type PresentationArgs = {
  presentation?: string;
  presentationContent?: string;
  presentationStartDate?: string;
  newPresentationTitle?: string;
};

// Error handling types
export type ErrorTemplate =
  | { single: (s: string) => string; multiple: (s: string) => string }
  | { format: (s: string, capitalize: boolean) => string };

export interface UpdateProcessConfig<TItem, TFailure> {
  isItemInError: (item: TItem, errorText: string) => boolean;
  createFailure: (item: TItem, reason: FailureReason) => TFailure;
  getItemFromFailure: (failure: TFailure) => TItem;
  getReasonFromFailure: (failure: TFailure) => FailureReason;
  getDisplayName: (item: TItem) => string | undefined;
  errorTemplates: Record<FailureReason, ErrorTemplate>;
  buildSuccessMessage: (
    addSucceeded: TItem[],
    removeSucceeded: TItem[],
  ) => string;
  extractNamesFromError: (rawMsg: string) => string[];
  getNameFromItem: (item: TItem) => string | undefined;
  formatNamesForError?: (names: string[]) => string[];
  matchCardData: (requested: TItem, result: TItem) => boolean;
  errorMessage: string;
  partialSuccessMessage: (successMsg: string, failureMsg: string) => string;
}

export interface OperationValidation<TItem, TFailure> {
  addSucceeded: TItem[];
  addFailed: TFailure[];
  removeSucceeded: TItem[];
  removeFailed: TFailure[];
}

export interface ValidationState<TItem, TFailure> {
  validation: OperationValidation<TItem, TFailure>;
  allFailures: TFailure[];
  parsingFailed: boolean;
  hasSuccess: boolean;
  hasFailures: boolean;
}
